import { Component, Inject, Input } from '@angular/core';

import { HttpErrorResponse } from "@angular/common/http";
import { Observable, BehaviorSubject, ReplaySubject, Subject, combineLatest, forkJoin, merge, of } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, startWith, tap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';

import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { isSame, isSameArray } from '../../../../shared/basic-data-management/has-id.interface';
import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { LocalDate } from '../../../../shared/jsr310-localdates/types/local-date.type';
import { LocalTime } from '../../../../shared/jsr310-localdates/types/local-time.type';
import { LocalDateTime } from 'src/main/angular/app/shared/jsr310-localdates/types/local-date-time.type';
import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';

import { OuvrageService } from '../../../structure/ouvrage/services/ouvrage.service';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { AntenneService } from '../../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';

import { EntrepriseService } from '../../../entreprises/services/entreprise.service';
import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';

import { IntervenantService } from '../../../humain/intervenant/services/intervenant.service';
import { IntervenantTLR } from '../../../humain/intervenant/entities/intervenant-tlr.entity';

import { MemoService } from '../../../memo/services/memo.service';
import { Memo } from '../../../memo/entities/memo.entity';

import { MotifRefusService } from '../services/motif-refus.service';
import { MotifRefus } from '../entities/motif-refus.entity';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { UserService } from '../../../humain/user/services/user.service';
import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { AccesControleSecurite } from '../../../../static-types/entities/acces-controle-securite.entity';
import { NiveauAlerteIncendie } from '../../../../static-types/entities/niveau-alerte-incendie.entity';

import { CoactiviteEolienCheckCommand } from './coactivite-eolien-checker.component';
import { CoactiviteSolaireCheckCommand } from './coactivite-solaire-checker.component';

import { OuvrageBySousParcMultiSelectorWithNotesReinitData } from '../../../structure/ouvrage/components/ouvrage-by-sous-parc-multi-selector-with-notes.component';

import { BasicAccesOrder, ParticipantAcces } from '../entities/basic-acces-order.entity';
import { CoactiviteDecision } from '../entities/coactivite-decision.constants';
import { CreationAccesOrder } from '../entities/creation-acces-order.entity';
import { CreationDeniedAccesOrder } from '../entities/creation-denied-acces-order.entity';
import { CreationGrantedAccesOrder } from '../entities/creation-granted-acces-order.entity';
import { CreationPlannedAccesOrder } from '../entities/creation-planned-acces-order.entity';
import { UpdateAccesOrder } from '../entities/update-acces-order.entity';

type DisplayedMemo = {
	entity: Memo,
	categorie: string,
}

type RadioOption<T> = {
	value: T,
	label: string,
}

// Fonction d'aide utilisation dans les fonction d'enregistrement
let notNull = <T>(value: T | null, nom: string) => {
	if (value === null || value === undefined) {
		alert('Le champ ' + nom + ' ne peut pas être vide.');
		throw new Error('Le champ ' + nom + ' ne peut pas être vide.');
	}
	return value;
}

var arrayEquals = <T>(a: T[] | null, b: T[] | null) => {
	if (a === null) {
		return b == null;
	}

	if (b === null)
		return false;

	return a.length === b.length && a.every((val, index) => val === b[index]);
};

var isSolaire = (parc: ParcTLR | null | undefined) => !!parc && parc.energie === "Solaire";
var getAllowedClasses = (parc: ParcTLR) =>  parc.typeOnduleur ? ["PDL", "PDC", "GPO"] : ["PDL", "PDC", "OND", "PS"];

var allowLog = false;

var _logTap = <T> (label : string) => tap(
	(data: T) => {
		if (allowLog)
			console.log("edit-acces - " + label + ": ", data);
	}
);

@Component({
	selector: 'edit-acces-conduite',
	templateUrl: 'edit-acces-conduite.html',
	styleUrls: ['edit-acces-conduite.css'],
})
export class EditAccesConduiteComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	loaded = false;
	waitingForServer = false;
	antenneResetCommands!: Observable<number | null | undefined>;
	reinitOuvragesFieldCommands$ = new BehaviorSubject<OuvrageBySousParcMultiSelectorWithNotesReinitData | null>(null);
	modeSolaireLocal$!: Observable<boolean>;
	allowsOuvragesConcernes$!: Observable<boolean>;
	reinitOuvragesConcernesFieldCommands$ = new BehaviorSubject<OuvrageBySousParcMultiSelectorWithNotesReinitData | null>(null);
	// NB: ici "null" = les valeurs n'ont pas encore ete chargee pour l'antenne selectionnee
	resetDateCommands = new Subject<void>();
	private errorMessages: string[] = [];
	userCanGrantAcces$!: Observable<boolean>;
	checkCoactiviteEolienCommands = new Subject<CoactiviteEolienCheckCommand>();	
	checkCoactiviteSolaireCommands = new Subject<CoactiviteSolaireCheckCommand>();
	maxOuvrageSelectables$!: Observable<number | null>;

	// Listes d'autorite statiques a bind sur la vue
	typeOptions: RadioOption<string>[] = [
		{
			value: 'LOCAL',
			label: 'Local',
		},
		{
			value: 'A_DISTANCE',
			label: 'A distance',
		},
	];
	ouiNonOptions: RadioOption<boolean>[] = [
		{
			value: true,
			label: 'Oui',
		},
		{
			value: false,
			label: 'Non',
		},
	];
	controlesSecurite$!: Observable<AccesControleSecurite[]>
	motifsRefus$!: Observable<MotifRefus[]>;

	// Donnees dynamiques lier sur la vue
	title!: string;
	user!: UserTLR;
	mode!: string;
	acces!: AccesTLR | null;
	responsableParc!: IntervenantTLR | null;
	displayedMemosParc: DisplayedMemo[] = [];
	niveauAlertIncendie$: Observable<NiveauAlerteIncendie | null>;
	entreprisesSelectionables: EntrepriseTLR[] = [];
	intervenantsSelectionables: IntervenantTLR[] = [];
	intervenantsSelectionablesByEntreprise: IntervenantTLR[][] = [];
	numero$ = new BehaviorSubject<string>('');
	dateTimeDebutJS$!: Observable<Date | null>;

	// Saisies de l'utilisateur
	antenne$ = new BehaviorSubject<AntenneTLR | null>(null);
	parc$ = new BehaviorSubject<ParcTLR | null>(null);
	ouvrages$ = new BehaviorSubject<OuvrageTLR[]>([]);
	ouvragesConcernes$ = new BehaviorSubject<OuvrageTLR[]>([]);
	typeAcces$ = new BehaviorSubject<string>('A_DISTANCE');
	heureDebut$ = new ReplaySubject<LocalTime>(1);
	dateDebut$ = new BehaviorSubject<LocalDate | null>(null);
	commentaire!: string;
	lierIntervention!: boolean;
	accesCegex$ = new BehaviorSubject<boolean>(true);
	astreinte$ = new BehaviorSubject<boolean|null>(null);
	controleSecurite: number | null = null;
	motifRefus: number | null = null;
	associatedIntervenant$ = new BehaviorSubject<IntervenantTLR | null>(null);
	creationImpossible$ = new BehaviorSubject<boolean>(false);
	isUserCac:boolean = false;
	isUserCc:boolean = false;
	
	// Information concatenee pour la saisie de la date/heure de debut
	dateHeureDebut$ = new BehaviorSubject<LocalDateTime | null>(null);

	constructor(
		private dialog: MatDialog,

		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,

		@Inject(AccesService) private accesService: AccesService,
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(EntrepriseService) entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(MemoService) private memoService: MemoService,
		@Inject(MotifRefusService) motifRefusService: MotifRefusService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(OuvrageService) private ouvrageService: OuvrageService,
		@Inject(UserService) private userService: UserService,

		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		/////////////////////////////////////////////////////////////////////////////////////////
		// Versions 'distinct' des observables de saisie, pour eviter les traitements doublons //
		/////////////////////////////////////////////////////////////////////////////////////////

		let distinctAntenne$ = this.antenne$.pipe(
			_logTap("antenne$"),
			distinctUntilChanged(isSame),
			_logTap("distinctAntenne$"),
		);

		let distinctParc$ = this.parc$.pipe(
			distinctUntilChanged(isSame),
		);

		let distinctOuvrages$ = this.ouvrages$.pipe(
			_logTap("ouvrages$"),
			distinctUntilChanged(isSameArray),
			_logTap("distinctOuvrages$"),
		);

		let distinctTypeAcces$ = this.typeAcces$.pipe(
			distinctUntilChanged(),
		);

		let distinctHeureDebut$ = this.heureDebut$.pipe(
			distinctUntilChanged(arrayEquals)
		);

		let distinctDateDebut$ = this.dateDebut$.pipe(
			distinctUntilChanged(arrayEquals)
		);

		let distinctAccesCegex$ = this.accesCegex$.pipe(
			distinctUntilChanged(),
		);

		//////////////////////////////////////////
		// Correspondances simple d'observables //
		//////////////////////////////////////////

		this.antenneResetCommands = distinctAntenne$.pipe(
			distinctUntilChanged(isSame),
			map($ => $ as ParcTLR | null),
			map(
				(antenne) => antenne == null ? null : antenne.id
			)
		);

		////////////////////////////////////////////////////////////////////////////////////
		// Lancement des pre-chargement de donnees "statiques" a la creation du composant //
		////////////////////////////////////////////////////////////////////////////////////

		// Chargement l'utilisateur courrant
		let user$ = this.userService.getCurrentUserTLR();

		// Chargement l'antenne de l'utilisateur
		let antenneOfUser$ = user$.pipe(
			mergeMap(
				(user) => user.role === 'CCE' ? of(null) : antenneService.getAntenneTLRById(user.antenneId)
			)
		);

		// Chargement des antennes
		let antennesById$ = antenneService.getAntenneTLRMapById().pipe(
			map(
				(map) => new MandatoryMap<number, AntenneTLR>(map, "Antenne")
			)
		);

		// Chargement des classes et sous-classes de memos
		let memoCategories$ = staticTypeService.getMemoCategoryMapById().pipe(
			map(
				(mcMap) => new MandatoryMap(mcMap, "MemoCategory")
			)
		);
		let memoSubCategories$ = staticTypeService.getMemoSubCategoryMapById().pipe(
			map(
				(mcMap) => new MandatoryMap(mcMap, "MemoSubCategory")
			)
		);

		// Chargement des classes d'ouvrages SOL et OND
		let classesOuvrages$ = ouvrageService.getClasseOuvrageList();
		let classesOuvragesMap$ = classesOuvrages$.pipe(
			map(
				(classes) => {
					let map = new Map<string, number>();
					for (let classe of classes) {
						map.set(classe.code, classe.id);
					}
					
					return new MandatoryMap<string, number>(map, "classeOuvrage");
				}
			)
		);

		// Chargement des controles de securite
		this.controlesSecurite$ = staticTypeService.getAccesControleSecuriteList();
		
		// Chargement des motifs de refus
		this.motifsRefus$ = motifRefusService.getList();
		
		// Chargement des niveaux d'alerte incendie
		let niveauxAlerteIncendie$ = staticTypeService.getNiveauAlerteIncendieMapById().pipe(
			map(
				(values) => new MandatoryMap<number, NiveauAlerteIncendie>(values, "NiveauAlerteIncendie")
			)
		);
		
		/////////////////////////////////////
		// Definition des valeurs derivees //
		/////////////////////////////////////

		// La date complete de debut en version JS est calculee depuis l'heure et la date de debut au format Java
		let dateTimeDebut$ = combineLatest([distinctHeureDebut$, distinctDateDebut$]).pipe(map(
			(data) => {
				let [heureDebut, dateDebut] = data;

				if (dateDebut == null)
					return null;

				return conversionService.merge(dateDebut, heureDebut);
			}
		));
		dateTimeDebut$.subscribe(
			(date) => this.dateHeureDebut$.next(date)
		);
		this.dateTimeDebutJS$ = dateTimeDebut$.pipe(map(
			(dateTimeDebut) => {
				return conversionService.fromNullableLocalDateTime(dateTimeDebut);
			}
		));

		// L'astreinte est une donne derivee de la date+heure de debut
		this.dateTimeDebutJS$.subscribe(
			(dateTimeDebutJS) => {
				let dateRef = dateTimeDebutJS ? dateTimeDebutJS : new Date();

				let jourSemaine = dateRef.getDay(); // 0 = Dimanche
				let jourOuvre = jourSemaine >= 1 && jourSemaine <= 5;
				let heure = dateRef.getHours();
				let heureOuvree = heure >= 8 && heure < 18;
				
				this.astreinte$.next(!jourOuvre || !heureOuvree);
			}
		);

		// Les memos du parc sont fonction du parc selectionne et de la date de debut
		let memosParc$ = combineLatest([distinctParc$, distinctDateDebut$]).pipe(mergeMap(
			(data) => {
				let [parc, dateRef] = data;

				// En cas d'absence d'un des deux parametres, on considere qu'il n'y a pas de memo
				if (!parc || !dateRef) {
					return of([] as Memo[]);
				}

				let dateParam = this.conversionService.toLocalDateParam2(dateRef);
				return this.memoService.getMemosOnParcForDate(parc.id, dateParam);
			}
		));

		// L'affichage des memos du parc depends de ces memo et des proprietes des categories/sous-categogies
		// (Par souci de simplicite d'ecriture cote vue, on fige le resultats en tant que propriete
		// plutot que faire un autre observable. Ce qui ne pose pas de souci tant qu'on ne fait rien d'autre
		// avec ces memos que les afficher)
		combineLatest([memosParc$, memoCategories$, memoSubCategories$]).subscribe(
			(data) => {
				let [memos, memoCategories, memoSubCategories] = data;

				let sortedMemos = memos.sort((a, b) => a.niveauId - b.niveauId);
				this.displayedMemosParc = sortedMemos.map(
					(memo) => {
						let categorie = '';

						if (memo.categorieId) {
							categorie = memoCategories.get(memo.categorieId).label;
							if (memo.sousCategorieId) {
								categorie = categorie + ' (' + memoSubCategories.get(memo.sousCategorieId).label + ') '
							}
							categorie += ' - ';
						}

						return {
							entity: memo,
							categorie: categorie,
						} as DisplayedMemo;
					}
				);
			}
		);

		// Lors d'un changement d'antenne
		let newNumeroAcces$ = distinctAntenne$.pipe(
			filter(
				(_antenne) => this.acces === null
			),
			mergeMap(
				(antenne) => antenne ? accesService.getNumeroAcces(antenne.id) : of('')
			),
			_logTap("newNumeroAcces$"),
		);
		newNumeroAcces$.subscribe(
			(newNumero) => this.numero$.next(newNumero)
		);

		// Sert a n'avoir que les transitions entre si le type d'acces est actuellement distant
		// ou non. Cela permet de ne pas declencher d'evenements inutiles quand l'utilisateur
		// passe de typeAcces = null  a typeAcces = 'local'
		let isAccesDistant$ = distinctTypeAcces$.pipe(
			map(
				(typeAcces) => typeAcces === "A_DISTANCE"
			),
			distinctUntilChanged(),
		);
		
		// Niveau d'alerte incendie
		this.niveauAlertIncendie$ = combineLatest([niveauxAlerteIncendie$, this.parc$]).pipe(
			map(
				([niveauxAlerteIncendie, parc]) => {
					if (parc === null) {
						return null;
					}
					
					if (parc.niveauIncendie == null) {
						return null;
					}
					
					return niveauxAlerteIncendie.get(parc.niveauIncendie);
				}
			)
		);


		////////////////////////////////////////////////////////////////////
		// Petite action utilisateur aussi utilisee dans l'initialisation //
		////////////////////////////////////////////////////////////////////

		this.resetDateCommands.subscribe(
			() => {
				// Evol 54010: On ne fait plus l'arrondi aux 5mn les plus proches
				// let dateDebutJS = dateService.getCurrentRoundedDateAndTime();
				let dateDebutJS = new Date();
				dateDebutJS.setSeconds(0);
				dateDebutJS.setMilliseconds(0);
				
				this.dateDebut$.next(this.conversionService.toMandatoryLocalDate(dateDebutJS));
				this.heureDebut$.next(this.conversionService.toMandatoryLocalTime(dateDebutJS));
			}
		);

		///////////////////////////////////////////////
		// Calculs de validation sur les observables //
		///////////////////////////////////////////////

		// NB: Ces controles sont ceux qu'on pourrait afficher dans tous les cas, des la saisie du champ
		// pour les controles specifiques a une operation, voir la fonction de validation ad-hoc 

		let errorMessages$: Observable<string | null>[] = [];

		let parcErrorMessage$ = distinctParc$.pipe(
			map(
				(parc) => parc === null ? "Veuillez sélectionner un parc." : null
			)
		);
		errorMessages$.push(parcErrorMessage$);
		
		let ouvragesErrorMessage$ = distinctOuvrages$.pipe(
			map(
				(ouvrages) => (ouvrages === null || ouvrages.length === 0) ? "Veuillez sélectionner un ouvrage." : null
			)
		);
		errorMessages$.push(ouvragesErrorMessage$);

		let typeAccesErrorMessage$ = distinctTypeAcces$.pipe(
			map(
				(typeAcces) => typeAcces == null ? "Veuillez choisir un type." : null
			)
		);
		errorMessages$.push(typeAccesErrorMessage$);

		let dateDebutErrorMessage$ = this.dateTimeDebutJS$.pipe(
			map(
				(dateDebut) => dateDebut === null ? "Veuillez renseigner complètement la date de début." : null
			)
		);
		errorMessages$.push(dateDebutErrorMessage$);

		let accesCegexErrorMessage$ = distinctAccesCegex$.pipe(
			map(
				(accesCegex) => accesCegex == null ? "Veuillez remplir la valeur pour \"Accès Cegex\"." : null
			)
		);
		errorMessages$.push(accesCegexErrorMessage$);

		combineLatest(errorMessages$).subscribe(
			(errorMessages: (string | null)[]) => this.errorMessages = errorMessages.filter($ => $ != null) as string[]
		);

		/////////////////////////////////////////////////////////
		// Calcul des autorisation d'utilisation des fonctions //
		/////////////////////////////////////////////////////////

		let userResponsibilitiesOnSelectedOuvrages$ = combineLatest([user$, distinctOuvrages$]).pipe(
			map(
				(data) => {
					let [user, ouvrages] = data;
					return {
						isCac: ouvrages.length > 0 && !ouvrages.some($ => $.cacId != user.id),
						isCc: ouvrages.length > 0 && !ouvrages.some($ => $.ccId != user.id)
					};
				}
			)
		);
		this.userCanGrantAcces$ = combineLatest([user$, userResponsibilitiesOnSelectedOuvrages$]).pipe(
			map(
				(data) => {
					let [user, userResponsibilitiesOnSelectedOuvrages] = data;
					this.isUserCac = userResponsibilitiesOnSelectedOuvrages.isCac;
					this.isUserCc = userResponsibilitiesOnSelectedOuvrages.isCc;
					// Il n'est possible d'autoriser un acces que si l'utilisateur connecte est considere comme CAC ou CC par rapport a l'ensemble du perimetre demande dans l'acces
					return (user.role === 'EXP' || user.role === 'CCE') && userResponsibilitiesOnSelectedOuvrages.isCac || (userResponsibilitiesOnSelectedOuvrages.isCc && this.typeAcces$.getValue() === 'A_DISTANCE');
				}
			)
		);

		//////////////////////////////////////////////
		// Evenements de saisie sur des observables //
		//////////////////////////////////////////////

		distinctAntenne$.subscribe(
			(_antenne) => {
				this.parc$.next(null);
			}
		);

		// Remise en place des biding antenne et responsable quand le parc change
		distinctParc$.pipe(
			withLatestFrom(distinctAntenne$, antennesById$)
		).subscribe(
			(data) => {
				let parc = data[0];
				let antenne = data[1];
				let antennesById = data[2];

				this.responsableParc = null;
				this.ouvragesConcernes$.next([]);

				if (parc && parc.responsableId) {
					// Verifier si l'antenne est selectionnee, sinon, le faire automatiquement
					// (cf Redmine #33419 RG2)
					if (antenne == null) {
						let antenneOfParc = antennesById.get(parc.antenneId);
						this.antenne$.next(antenneOfParc);
					}

					// Chargement des donnees sur le responsable du parc
					this.intervenantService.getIntervenantTLRById(parc.responsableId).subscribe(
						(intervenant) => this.responsableParc = intervenant
					);
				}
			}
		);
		
		/////////////////////////////////
		// Gestion du champ "Ouvrages"
		
		this.maxOuvrageSelectables$ = combineLatest([distinctParc$, isAccesDistant$]).pipe(
			map(
				([parc, isAccesDistant]) => (isSolaire(parc) && isAccesDistant) ? null : 1
			),
			startWith(1),
		);
		
		this.modeSolaireLocal$ = combineLatest([distinctParc$, isAccesDistant$]).pipe(
			map(
				([parc, isAccesDistant]) =>  isSolaire(parc) && !isAccesDistant
			),
			startWith(false),
			_logTap("modeSolaireLocal$"),
		);
		
		// Cas 1 - Quand l'utilisateur selectionne un parc non-solaire
		let parcSelectedEvents$ = distinctParc$.pipe(
			filter(
				(parc) => !!parc
			),
			map(
				(parc) => parc as ParcTLR
			),
		);
		let reinitOuvragesFieldEvent1$ = parcSelectedEvents$.pipe(
			filter(
				(parc) => !isSolaire(parc)
			),
			map(
				// Pour ce cas, on ne filtre aucun ouvrage et on ne selectionne rien
				(parcNonSolaire) => ({
					parc: parcNonSolaire,
					selectedOuvrageIds: [],
				} as OuvrageBySousParcMultiSelectorWithNotesReinitData)
			),
			_logTap("reinitOuvragesFieldEvent1$"),
		);

		// Cas 2 - A: Quand l'utilisateur selectionne un parc solaire et que le type d'acces est local
		//      OU B: quand l'utilisateur slectionner le type d'acces "local" alors qu'un parc solaire est selectionne
		let parcSolaireSelectedEvent$ = parcSelectedEvents$.pipe(
			filter(
				(parc) => isSolaire(parc)
			),
			withLatestFrom(isAccesDistant$),
			map(
				([parc, isAccesDistant]) => ({
					parcSolaire: parc,
					isAccesDistant: isAccesDistant,
				})
			)
		);
		let reinitOuvragesFieldEvent2A$ = parcSolaireSelectedEvent$.pipe(
			filter(
				(data) => !data.isAccesDistant
			),
			map(
				(data) => data.parcSolaire
			),
			_logTap("reinitOuvragesFieldEvent2A$"),
		);
		let reinitOuvragesFieldEvent2B$ = isAccesDistant$.pipe(
			withLatestFrom(this.parc$),
			filter(
				([isAccesDistant, parc]) => !isAccesDistant && isSolaire(parc) 
			),
			map(
				(data) => data[1] as ParcTLR
			),
			_logTap("reinitOuvragesFieldEvent2B$"),
		);
		let reinitOuvragesFieldEvent2$ = merge(reinitOuvragesFieldEvent2A$, reinitOuvragesFieldEvent2B$).pipe(
			withLatestFrom(classesOuvragesMap$),
			map(
				([parcSolaire, classeOuvragesMap]) => {
					let classeSOL = classeOuvragesMap.get("SOL");
					
					// Pour ce cas, on selectionne et n'autorise que les ouvrages SOL
					// NB: la saisie du champ par l'utilisateur est normalement desactivee
					return {
						parc: parcSolaire,
						ouvrageFilter: (_parc: ParcTLR, ouvrage: OuvrageTLR) => ouvrage.classeOuvrage === classeSOL && this.user.id === ouvrage.ccId, // FIXME: injecter ici le CC_ID
						ouvrageSelector: (ouvrage: OuvrageTLR) => ouvrage.classeOuvrage === classeSOL,
					} as OuvrageBySousParcMultiSelectorWithNotesReinitData;
				}
			),
			_logTap("reinitOuvragesFieldEvent2$"),
		);
		
		// Cas 3 - A: Quand l'utilisateur selectionne un parc solaire et que le type d'acces est distant
		//      OU B: Quand l'utilisateur selectionne le type d'acces distant et qu'un parc solaire est selectionne
		let reinitOuvragesFieldEvent3A$ = parcSolaireSelectedEvent$.pipe(
			filter(
				(data) => data.isAccesDistant
			),
			map(
				(data) => data.parcSolaire
			),
			_logTap("reinitOuvragesFieldEvent3A$"),
		);
		let reinitOuvragesFieldEvent3B$ = isAccesDistant$.pipe(
			withLatestFrom(this.parc$),
			filter(
				([isAccesDistant, parc]) => isAccesDistant && isSolaire(parc) 
			),
			map(
				([_isAccesDistant, parc]) => parc as ParcTLR
			),
			_logTap("reinitOuvragesFieldEvent3B$"),
		);
		let reinitOuvragesFieldEvent3$ = merge(reinitOuvragesFieldEvent3A$, reinitOuvragesFieldEvent3B$).pipe(
			withLatestFrom(classesOuvragesMap$),
			map(
				([parcSolaire, classeOuvragesMap]) => {
					let allowedClasses = getAllowedClasses(parcSolaire);
					let allowedClassesIds = allowedClasses.map($ => classeOuvragesMap.get($));
					let ouvrageFilter = (_parc: ParcTLR, ouvrage: OuvrageTLR) => { // FIXME: injecter ici le CC_ID
							let classe = ouvrage.classeOuvrage;
							return allowedClassesIds.some($ => $ === classe)  && this.user.id === ouvrage.ccId;
					};

					// Pour ce cas, on n'autorise que les ouvrages adapates au type d'onduleur
					// Et on tente de reselectionner les ouvrages precedemment selectionnes					
					return {
						parc: parcSolaire,
						ouvrageFilter: ouvrageFilter,
						selectedOuvrageIds: [],
					} as OuvrageBySousParcMultiSelectorWithNotesReinitData;
				}
			),
			_logTap("reinitOuvragesFieldEvent3$"),
		);

		// Transmission de la commande de reinitialisation au composant fils		
		let reinitOuvragesFieldEvents$ = merge(
			reinitOuvragesFieldEvent1$,
			reinitOuvragesFieldEvent2$,
			reinitOuvragesFieldEvent3$,
		);
		reinitOuvragesFieldEvents$.subscribe(
			(reinitData) => {
				// Uniquement si le composant a fini de se charger, on transmet
				// la commande de reinitialisation
				// (sinon c'est que les changements sont programmatifs et on va
				// attendre d'avoir aussi les ouvrages a preselectionner
				// -voir plus bas-)
				if (this.loaded) {
					this.reinitOuvragesFieldCommands$.next(reinitData);
				}
			}
		);

		// Si on tente d'initialiser les ouvrages programmativement
		let ouvragesSelectionCommands$ = new Subject<OuvrageTLR[]>();
		let programativeOuvragesSelectionEvents$ = ouvragesSelectionCommands$.pipe(
			// Alors on recupere la derniere commande d'initialisation
			withLatestFrom(reinitOuvragesFieldEvents$),
			// On la modifie pour inclure les ouvrages
			map(
				([ouvrages, reinitData]) => {
					reinitData.selectedOuvrageIds = ouvrages.map($ => $.id);
					reinitData.ouvrageSelector = undefined;
					return reinitData;
				}
			),
			_logTap("programativeOuvragesSelectionEvents$"),
		);
		programativeOuvragesSelectionEvents$.subscribe(
			(reinitData) => this.reinitOuvragesFieldCommands$.next(reinitData)
		);
	
		//////////////////////////////////////////
		// Gestion du champ "Ouvrages Concernes"
		
		let ouvragesConcernesRules$ = combineLatest([distinctParc$, isAccesDistant$, distinctOuvrages$]).pipe(
			withLatestFrom(classesOuvragesMap$),
			map(
				([[parc, isAccesDistant, ouvrages], classesOuvragesMap]) => {
					// Les parcs non-solaires centralisés n'ont pas d'ouvrages concernes
					if (!isSolaire(parc)) {
						return {
							display: false,
							filter: (_parc: ParcTLR, _ouvrage: OuvrageTLR) => false, // FIXME: injecter ici le CC_ID
						};
					}
					
					// Les parcs solaires en acces local ont droit a des ouvrages concernes
					if (!isAccesDistant) {
						let classeSOL = classesOuvragesMap.get("SOL");
						return {
							display: true,
							filter: (_parc: ParcTLR, ouvrage: OuvrageTLR) => ouvrage.classeOuvrage != classeSOL &&  this.user.id === ouvrage.ccId // FIXME: injecter ici le CC_ID
						}
					}
					
					// Evol 52295
					// Pour les parcs solaires en acces distant, cela depend s'il y a des ouvrages GPO selectionnes
					let classeGPO = classesOuvragesMap.get("GPO");
					let ouvragesGPO = ouvrages.filter($ => $.classeOuvrage === classeGPO);
					if (ouvragesGPO.length === 0) {
						return {
							display: false,
							filter: (_parc: ParcTLR, _ouvrage: OuvrageTLR) => false, // FIXME: injecter ici le CC_ID
						}
					}
					let ouvragesGPOIds = ouvragesGPO.map($ => $.id);
					return {
						display: true,
						filter: (_parc: ParcTLR, ouvrage: OuvrageTLR) => { // FIXME: injecter ici le CC_ID
							return ouvragesGPOIds.some($ => $ === ouvrage.ouvrageElecPere) && this.user.id === ouvrage.ccId;
						}
					};
				}
			),
			_logTap("ouvragesConcernesRules$"),
		);
		
		this.allowsOuvragesConcernes$ = ouvragesConcernesRules$.pipe(
			map(
				(rules) => rules.display
			)
		);
		
		// Quand on masque le champ ouvrage concerne, on le vide, pour etre sur
		// qu'il ne restera aucune reference lors d'une tentative d'enregistrement
		let ouvragesConcernesFieldDisabled$ = this.allowsOuvragesConcernes$.pipe(
			distinctUntilChanged(),
			filter(
				(display) => !display
			),
		);
		ouvragesConcernesFieldDisabled$.subscribe(
			() => {
				if (this.loaded) {
					this.ouvragesConcernes$.next([]);
				}
			}
		);
		
		// Reinitialisation du champ
		let reinitOuvragesConcernesFieldEvent$ = ouvragesConcernesRules$.pipe(
			filter(
				(rules) => rules.display
			),
			map(
				(rules) => rules.filter
			),
			withLatestFrom(this.parc$),
			filter(
				([_filter, parc]) => parc != null
 			),
 			_logTap("reinitOuvragesConcernesFieldEvent$"),
		);
		reinitOuvragesConcernesFieldEvent$.subscribe(
			([filter, parc]) => {
				// Uniquement si le composant a fini de se charger, on transmet
				// la commande de reinitialisation
				// (sinon c'est que les changements sont programmatifs et on va
				// attendre d'avoir aussi les ouvrages concernes a preselectionner
				// -voir plus bas-)
				if (this.loaded) {
					this.reinitOuvragesConcernesFieldCommands$.next({
						parc: parc as ParcTLR,
						ouvrageFilter: filter,
						selectedOuvrageIds: [],
					});
				}
			}
		);

		// Si on tente d'initialiser les ouvrages concernes programmativement
		let ouvragesConcernesSelectionCommands$ = new Subject<OuvrageTLR[]>();
		let programativeOuvragesConcernesSelectionEvents$ = ouvragesConcernesSelectionCommands$.pipe(
			// Alors on recupere la derniere commande d'initialisation
			withLatestFrom(reinitOuvragesConcernesFieldEvent$),
			_logTap("programativeOuvragesConcernesSelectionEvents$"),
		);
		programativeOuvragesConcernesSelectionEvents$.subscribe(
			([ouvragesConcernes, [filter, parc]]) => {
				this.reinitOuvragesConcernesFieldCommands$.next({
					parc: parc as ParcTLR,
					ouvrageFilter: filter,
					selectedOuvrageIds: ouvragesConcernes.map($ => $.id),
				});
			}
		);

		///////////////////////////////////////////////////////////////////////////////////////////////////////
		// Initialisation du composant lors de son affichage, a partir des informations de contexte fournies //
		///////////////////////////////////////////////////////////////////////////////////////////////////////
		
		////////////////////////////////////
		// Initialisaiton: ELEMENT COMMUNS
		
		let staticDataLoaded$ = user$.pipe(
			tap(
				(user) => {
					this.user = user;
					// Chargement des donnees sur le responsable du parc
					if (this.user) {
						this.intervenantService.getIntervenantTLRById(this.user.id).subscribe(
							(associatedIntervenant) => {
								this.associatedIntervenant$.next(associatedIntervenant);
							},
							(error: HttpErrorResponse) => {
								console.log('l\'utilisateur connecté n\'est pas un intervenant, erreur: ', error);
								this.loaded = true;
								this.creationImpossible$.next(true);
							}
						);
					}
				}
			),
			map(
				(_data) => {}
			)
		);
		
		// Operateurs nettoyant la vue des elements qui ont pu y etre mis par l'utilisateur precedent
		let clearViewBindings = () => {
			this.mode = "#error#";
			this.title = "#error#";
			this.acces = null;

			this.numero$.next('');
			this.antenne$.next(null);
			this.parc$.next(null);
			this.ouvrages$.next([]);
			this.ouvragesConcernes$.next([]);
			this.typeAcces$.next('A_DISTANCE');
			this.resetDateCommands.next();
			this.commentaire = '';
			this.lierIntervention = true;
			this.accesCegex$.next(true);
			this.motifRefus = null;
			this.controleSecurite = null;
			this.isUserCac = false;
			this.isUserCc = false;
			this.loaded = false;
		};

		
		/////////////////////////////////////
		// Initialisation - Mode: CREATION 
		
		// Recuperation de l'evenement via l'event bus
		let initAsCreationRequest$ = new Subject<void>();
		eventBusService.observe(Events.CreateAccesConduiteModalRequested, () => initAsCreationRequest$.next());
	
		// Enrichissement avec les donnnes necessaires		
		let initAsCreationWithUserDataRequest$ = combineLatest([initAsCreationRequest$, antenneOfUser$]).pipe(
			tap(clearViewBindings),
			map(
				(data) => data[1]
			)
		);
		
		// Initialisation des bindings de la vue pour le mode
		let initAsCreationFinalizationRequest$ = combineLatest([initAsCreationWithUserDataRequest$, staticDataLoaded$]).pipe(
			map(
				(data) => data[0]
			),
			tap(
				(antenneOfUser) => {
					this.mode = "creation";
					this.title = "Création d'accès conduite";
					this.acces = null;
	
					// Initialiser le numero en premier, car le changement d'antenne peut l'alterer ensuite
					this.numero$.next('');
	
					// Initialisation du bloc perimetre (mode creation)
					this.antenne$.next(antenneOfUser);
					this.parc$.next(null);
					this.ouvrages$.next([]);
					this.ouvragesConcernes$.next([]);
	
					// Initialisation du bloc acces (mode creation)
					this.typeAcces$.next('A_DISTANCE');
					this.resetDateCommands.next();
					this.commentaire = '';
					this.lierIntervention = true;
					this.accesCegex$.next(true);
	
					this.motifRefus = null;
					this.controleSecurite = null;
					this.isUserCac = false;
					this.isUserCc = false;
				}
			),
			map(
				(_data) => {}
			),
		);

		/////////////////////////////////
		// Initialisation - Finalisation 

		initAsCreationFinalizationRequest$.subscribe(
			() => { 
				this.motifRefus = null;
				this.controleSecurite = null;
	
				if (allowLog) {
					console.log("edit-acces - loaded => true ");
				}
				this.loaded = true;
				this.displaySubject.next(true);
			}
		);
	}

	@Input() id = 'editAccesConduitePopup';

	close() {
		this.displaySubject.next(false);
	}

	createNewPlannedAcces(withDemandeValidation: boolean) {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.olderThan(dateHeureDebut, 30)) {
			specificErrors.push("Vous ne pouvez pas plannifier un acces avec un debut déjà depassé depuis plus de 30 minutes.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		// Cree l'objet de commande pour la creation de l'acces
		let command = this.fillCreateCommandObject(new CreationPlannedAccesOrder());
		command.demanderValidation = withDemandeValidation;
		
		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.createAsPlanned(command).subscribe(
			(id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);

				// S'il faut associer des interventions au dit acces...
				if (this.lierIntervention) {
					// ... appeller la fenetre d'association d'un acces avec des interventions
					this.eventBusService.emit(Events.AssociationOperationsModalRequested, id);
				}
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de création de l'accès", error);
			}
		);
	}
	
	createNewGrantedAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.inFuture(dateHeureDebut)) {
			specificErrors.push("Vous ne pouvez pas valider un accès plus de 15 minutes dans le futur.");
		}

		// NB: le controle de securite est optionnel.

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		// Confier la suite des operations au checker de coactivite adapte
		let parcNN = notNull(this.parc$.getValue(), "parc");
		
		let decisionSubject = new Subject<CoactiviteDecision>();
		decisionSubject.subscribe(
			(decision) => {
				if (decision === CoactiviteDecision.AUTHORIZE) {
					this.persistNewGrantedAcces();
				} else if (decision === CoactiviteDecision.DENY) {
					this.motifRefus = 4;
					this.persistNewDeniedAcces();
				} else if (decision === CoactiviteDecision.ASK_VALIDATION) {
					this.createNewPlannedAcces(true);
				}
			}
		);

		let energie = parcNN.energie;
		if (energie === 'Solaire') {
			this.checkCoactiviteSolaireCommands.next({
				accesId: -1,
				ouvragesIds: this.ouvrages$.getValue().map($ => $.id),
				decisionSubject: decisionSubject,
				isUserCac: this.isUserCac,
				isUserCc: this.isUserCc
			});
		} else if (energie === 'Eolienne') {
			// NB: dans un parc eolien, il n'y a normalement qu'un seul ouvrage
			this.checkCoactiviteEolienCommands.next({
				accesId: -1,
				accesCegex: notNull(this.accesCegex$.getValue(), "accesCegex"),
				ouvrageId: notNull(this.ouvrages$.getValue(), "ouvrage")[0].id,
				decisionSubject: decisionSubject,
				isUserCac: this.isUserCac,
				isUserCc: this.isUserCc
			});
		}

		// On arrete le traitement ici en attendant la reponse du checker
	}

	private persistNewGrantedAcces() {
		// Cree l'objet de commande pour la creation de l'acces
		let order = this.fillCreateCommandObject(new CreationGrantedAccesOrder());
		order.controleSecurite = this.controleSecurite;
		let now = this.conversionService.toLocalDateTime(new Date());
		if (now != null) {
			order.dateAutorisation = now;
		}

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.createAsGranted(order).subscribe(
			(id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);

				// S'il faut associer des interventions au dit acces...
				if (this.lierIntervention) {
					// ... appeller la fenetre d'association d'un acces avec des interventions
					this.eventBusService.emit(Events.AssociationOperationsModalRequested, id);
				}
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de création de l'accès", error);
			}
		);
	}

	createNewDeniedAcces() {
		// Realise la validation des champs, specifique a l'operation de creation
		let specificErrors: string[] = [];

		let dateHeureDebut = this.dateHeureDebut$.getValue();
		if (dateHeureDebut && this.inFuture(dateHeureDebut)) {
			specificErrors.push("Vous ne pouvez pas refuser un accès plus de 15 minutes dans le futur.");
		}

		if (this.motifRefus == null) {
			specificErrors.push("Veuillez sélectionner un motif de refus.");
		}

		// Affiche les erreurs generique et specifiques, si besoin
		let hadErrors = this.displayValidationErrors(specificErrors);
		if (hadErrors)
			return;

		this.persistNewDeniedAcces();
	}

	private persistNewDeniedAcces() {
		// Cree l'objet de commande pour la creation de l'acces
		let order = this.fillCreateCommandObject(new CreationDeniedAccesOrder());
		order.motifRefus = notNull(this.motifRefus, "motifRefus");
		let now = this.conversionService.toLocalDateTime(new Date());
		if (now != null) {
			order.dateRefus = now;
		}

		// Appelle le serveur pour effectuer la creation
		this.waitingForServer = true;
		this.accesService.createAsDenied(order).subscribe(
			(_id) => {
				this.waitingForServer = false;
				this.displaySubject.next(false);

				// Signaler l'evenement de creation au bus d'evenement general				
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error: HttpErrorResponse) => {
				this.waitingForServer = false;
				this.dialog.open(MessageDialogComponent, {
					width: '400px',
					data: {
						title: "Erreur lors de la création de l'accès",
						messages: ['Le serveur a rencontré une erreur imprévue']
					}
				});
				console.error("Erreur lors la tentivate de création de l'accès", error);
			}

		);
	}


	private olderThan(date: LocalDateTime, nbMinutes: number): boolean {
		let dateRef = new Date();
		dateRef.setMinutes(dateRef.getMinutes() - nbMinutes);
		let dateDebutJS = this.conversionService.fromMandatoryLocalDateTime(date);
		return dateDebutJS.getTime() < dateRef.getTime();
	}

	// Determine si une date est plus de 15mn dans le futur
	private inFuture(date: LocalDateTime): boolean {
		let dateRef = new Date();
		dateRef.setMinutes(dateRef.getMinutes() + 15);
		let dateDebutJS = this.conversionService.fromMandatoryLocalDateTime(date);
		return dateDebutJS.getTime() > dateRef.getTime();
	}

	// Affiche les messages d'erreur de validation
	private displayValidationErrors(specificErrors: string[]): boolean {
		let errors: string[] = [...this.errorMessages, ...specificErrors];

		if (errors.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: 'Enregistrement impossible',
					messages: errors
				}
			});

			return true;
		}

		return false;
	}

	private fillCreateCommandObject<T extends CreationAccesOrder>(order: T): T {
		// Integrer les valeur des saisies en Observable
		order.parcId = notNull(this.parc$.getValue(), "parc").id;
		order.ouvrageIds = this.ouvrages$.getValue().map($ => $.id);
		order.ouvragesConcernesIds = this.ouvragesConcernes$.getValue().map($ => $.id);

		this.fillBasicCommandProperties(order);
		
		order.numero = this.numero$.getValue();
		order.typeAcces = notNull(this.typeAcces$.getValue(), "typeAcces");
		order.dateDebut = notNull(this.dateHeureDebut$.getValue(), "dateDebut");
		order.accesCegex = notNull(this.accesCegex$.getValue(), "accesCegex");

		return order;
	}

	private fillBasicCommandProperties<T extends BasicAccesOrder>(order: T): T {
		
		let participantsIntervenants = [{'role': 0, 'id': this.user.id}].map($ => ({
			role: $.role,
			id: $.id,
		} as ParticipantAcces));
		order.intervenants = participantsIntervenants;
		order.entreprises = [];
		order.commentaire = this.commentaire;
		order.astreinte = notNull(this.astreinte$.getValue(), "astreinte");

		return order;
	}

	
	public atLeastOneOuvrageSelected(): boolean {
		return this.ouvrages$.getValue() && this.ouvrages$.getValue().length > 0;
	}
}